import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { recruitmentAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { 
  Briefcase, 
  MapPin, 
  DollarSign, 
  Users,
  Search,
  Building2,
  Clock,
  CheckCircle,
  Send
} from 'lucide-react';
import { toast } from 'sonner';

const Recruitment = () => {
  const { user } = useAuth();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);

  const isHR = user?.role === 'supervisor' || user?.role === 'executive';

  useEffect(() => {
    fetchPositions();
  }, []);

  const fetchPositions = async () => {
    try {
      setLoading(true);
      const response = await recruitmentAPI.getPositions();
      setPositions(response.data);
    } catch (error) {
      console.error('Recruitment fetch error:', error);
      toast.error('Failed to load positions');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    try {
      await recruitmentAPI.apply({ position_id: selectedPosition?.id });
      toast.success('Application submitted successfully!');
      setShowApplyDialog(false);
      setSelectedPosition(null);
    } catch (error) {
      console.error('Apply error:', error);
      toast.error('Failed to submit application');
    }
  };

  const openApplyDialog = (position) => {
    setSelectedPosition(position);
    setShowApplyDialog(true);
  };

  const filteredPositions = positions.filter(pos =>
    pos.position?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    pos.department?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getTypeColor = (type) => {
    switch (type) {
      case 'internal': return 'bg-blue-100 text-blue-700';
      case 'external': return 'bg-violet-100 text-violet-700';
      case 'both': return 'bg-emerald-100 text-emerald-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4" />
          <p className="text-slate-500">Loading positions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="recruitment-page">
      {/* Header */}
      <Card className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-none">
        <CardContent className="p-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center">
              <Briefcase className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {isHR ? 'Recruitment Management' : 'Career Opportunities'}
              </h2>
              <p className="text-blue-100">
                {isHR ? 'Manage open positions and applications' : 'Explore internal job opportunities'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white border-none">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-100 text-sm">Open Positions</p>
                <p className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {positions.filter(p => p.status === 'open').length}
                </p>
              </div>
              <Briefcase className="w-10 h-10 text-indigo-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Departments Hiring</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {[...new Set(positions.map(p => p.department))].length}
                </p>
              </div>
              <Building2 className="w-10 h-10 text-violet-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Total Applicants</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {positions.reduce((sum, p) => sum + (p.applicants || 0), 0)}
                </p>
              </div>
              <Users className="w-10 h-10 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Search positions..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-white"
          data-testid="recruitment-search"
        />
      </div>

      {/* Positions Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredPositions.map((position) => (
          <Card key={position.id} className="card-hover" data-testid={`position-card-${position.id}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <Badge className={getTypeColor(position.type)}>
                  {position.type === 'both' ? 'Internal & External' : position.type?.charAt(0).toUpperCase() + position.type?.slice(1)}
                </Badge>
                <Badge className={position.status === 'open' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                  {position.status?.charAt(0).toUpperCase() + position.status?.slice(1)}
                </Badge>
              </div>

              <h3 className="font-semibold text-lg text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {position.position}
              </h3>
              
              <p className="text-sm text-slate-500 mb-4 line-clamp-2">
                {position.description}
              </p>

              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Building2 className="w-4 h-4 text-slate-400" />
                  {position.department}
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  {position.location}
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <DollarSign className="w-4 h-4 text-slate-400" />
                  {position.salary_range}
                </div>
              </div>

              {position.requirements && position.requirements.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-medium text-slate-500 mb-2">Requirements:</p>
                  <div className="flex flex-wrap gap-1">
                    {position.requirements.slice(0, 3).map((req, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {req}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                <div className="flex items-center gap-1 text-sm text-slate-500">
                  <Users className="w-4 h-4" />
                  {position.applicants || 0} applicants
                </div>
                {position.status === 'open' && !isHR && (
                  <Button 
                    size="sm" 
                    className="bg-indigo-600 hover:bg-indigo-700"
                    onClick={() => openApplyDialog(position)}
                    data-testid={`apply-btn-${position.id}`}
                  >
                    <Send className="w-4 h-4 mr-1" />
                    Apply
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredPositions.length === 0 && (
        <div className="text-center py-12">
          <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No Positions Found</h3>
          <p className="text-slate-500">Try adjusting your search or check back later</p>
        </div>
      )}

      {/* Apply Dialog */}
      <Dialog open={showApplyDialog} onOpenChange={setShowApplyDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Apply for Position</DialogTitle>
            <DialogDescription>
              Submit your application for {selectedPosition?.position}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-slate-50 p-4 rounded-lg mb-4">
              <h4 className="font-semibold text-slate-900 mb-2">{selectedPosition?.position}</h4>
              <p className="text-sm text-slate-600">{selectedPosition?.department}</p>
              <p className="text-sm text-indigo-600 mt-2">{selectedPosition?.salary_range}</p>
            </div>
            <div className="space-y-2 text-sm text-slate-600">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                Your profile will be attached to the application
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                HR will review your application
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-500" />
                Expected response within 5 business days
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApplyDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleApply}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="confirm-apply-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Submit Application
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Recruitment;
